import { Button, IconButton, Tooltip } from "@material-ui/core";
import {
  ArrowDropDown,
  ArrowDropUp,
  InfoRounded,
  Remove,
} from "@material-ui/icons";
import { CommandEletronicExtract } from "Infrastructure/repositories/api/ExtractRepository";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import {
  ResolutionType,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";
import Toolbar from "areas/_catalog/components/toolbar/Toolbar";
import { moneyMask } from "corss-cutting/masks/money";
import React, { FC, useCallback, useMemo, useState } from "react";
import { hideCpf, toDateAndTime } from "utils/utils";
import styles from "./PospaidExtract.module.scss";
import { useTranslation } from "i18n/useTranslation";

interface IPospaidExtract {
  extract: CommandEletronicExtract;
  onClickPayment: () => void;
  serviceRate: number | null;
  serviceRatePercent?: number;
}

export const PospaidExtract: FC<IPospaidExtract> = ({
  extract,
  onClickPayment,
  serviceRate,
  serviceRatePercent,
}) => {
  const { kiosk } = useKiosk();
  const { resolution } = useScreenStore();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);

  const handleTooltipToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const showPaymentButton = useMemo(
    () =>
      extract.totalToPayWithFee > 0 &&
      (kiosk?.paymentVoucher ||
        kiosk?.paymentCredit ||
        kiosk?.paymentDebit ||
        kiosk?.paymentPix),
    [extract.totalToPayWithFee, kiosk]
  );

  return (
    <div
      id={styles.PospaidExtract}
      style={{
        marginTop: resolution === ResolutionType.FULL_HD ? "0" : "-100px",
      }}
    >
      <Toolbar hideWelcome title={kiosk?.localName ?? ""} />

      <div className={styles.user}>
        <p
          style={{ fontSize: resolution === ResolutionType.FULL_HD ? 35 : 25 }}
        >
          {extract.user?.name}
        </p>
        <p
          style={{ fontSize: resolution === ResolutionType.FULL_HD ? 25 : 18 }}
        >
          {hideCpf(extract.user?.document ?? "")}
        </p>
      </div>

      <div className={styles.content}>
        <div
          style={{
            maxHeight: resolution === ResolutionType.FULL_HD ? 1400 : 830,
          }}
        >
          {extract.orders.map((it, key) => (
            <div key={key} className={styles.item}>
              <div>
                <span>{toDateAndTime(String(it.createdAt))}</span>
                <span>
                  {it.items.reduce(
                    (acc, curr) =>
                      acc +
                      (!curr.isCanceled && !curr.isTransfered
                        ? curr.totalValue
                        : 0),
                    0
                  ) === 0 ? (
                    <Remove style={{ color: "#a2a2a2" }} />
                  ) : !it.isPayment ? (
                    <ArrowDropUp style={{ color: "#C00D1E" }} />
                  ) : (
                    <ArrowDropDown style={{ color: "#399C54" }} />
                  )}
                </span>
              </div>
              <>
                {it.items.map((item, _key) => (
                  <div
                    key={_key}
                    style={{
                      textDecoration: item.isCanceled
                        ? "line-through"
                        : undefined,
                      color: item.isCanceled
                        ? "#ED0029"
                        : item.isTransfered
                        ? "#a2a2a2"
                        : undefined,
                    }}
                  >
                    <span>{`${item.quantity}x ${item.name} ${
                      item.isTransfered ? t("cashless.transferred") : ""
                    }`}</span>
                    <span>{`${10 < 0 ? "- " : ""}${moneyMask(
                      item.totalValue.toFixed(2)
                    )}`}</span>
                  </div>
                ))}
                <div>
                  <span>
                    <b>{t("cashless.total")}</b>
                  </span>
                  <span>
                    <b>
                      {moneyMask(
                        it.items
                          .reduce(
                            (acc, curr) =>
                              acc +
                              (!curr.isCanceled && !curr.isTransfered
                                ? curr.totalValue
                                : 0),
                            0
                          )
                          .toFixed(2)
                      )}
                    </b>
                  </span>
                </div>
              </>
            </div>
          ))}
        </div>

        <div>
          <div className={styles.item}>
            <div />
            <div>
              <span>{t("cashless.pospaid.subtotal")}</span>
              <span>{moneyMask(extract.totalToPayWithoutFee.toFixed(2))}</span>
            </div>
            {serviceRate ? (
              <div>
                <span style={{ display: "flex", alignItems: "center" }}>
                  {kiosk?.localId === "2fa30635-8760-6d44-e5b6-d2cc3e5b2c66" // Bar Santa Julia
                    ? t("cashless.pospaid.service_fee_optional_label")
                    : t("cashless.pospaid.service_fee_label")}
                  <Tooltip
                    title={t("cashless.pospaid.service_fee_tooltip", { percent: (serviceRatePercent ?? 0) * 100 }) ?? ''}
                    placement="top"
                    open={open}
                  >
                    <IconButton onClick={handleTooltipToggle}>
                      <InfoRounded
                        style={{ color: "#a2a2a2", margin: "4px 0 0 4px" }}
                      />
                    </IconButton>
                  </Tooltip>
                </span>
                <span>{moneyMask(serviceRate.toFixed(2))}</span>
              </div>
            ) : (
              <></>
            )}
          </div>
          <div className={styles.footer}>
            <div>
              <span>
                <b>{t("cashless.pospaid.total_to_pay")} </b>
              </span>
              <span style={{ color: "#399C54" }}>
                <b>{moneyMask(extract.totalToPayWithFee.toFixed(2))}</b>
              </span>
            </div>

            {showPaymentButton && (
              <Button
                variant="contained"
                fullWidth
                onClick={onClickPayment}
                style={{ backgroundColor: "#1a5" }}
              >
                {t("cashless.buttons.pay_account")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
